/**
 * The Desktop's arrangement — where one person put each icon and each window.
 * Pure: no React, no network.
 *
 * Arrangement is NOT membership. Moving an icon inside its container changes
 * only this; which folder a file is in is `vault_documents.folder_id`, changed
 * by a real move. A position is recorded against its container, so a file
 * moved elsewhere simply has no position in its new folder and auto-arranges
 * there — nothing here has to follow a move made in another view.
 *
 * A window has its own id and shows one folder at a time (`folder`):
 * opening a folder inside it navigates it. How a folder's contents are laid
 * out — icon size or list, and sort — is its VIEW, kept per container so a
 * folder opens the way it was left, in any window.
 *
 * The server stores the same shape (`document_desktop_layouts.layout`) and
 * merges patches into it; the limits below mirror its schema.
 */

export interface Cell {
  /** Column, 0-based. */
  c: number;
  /** Row, 0-based. */
  r: number;
}

export interface WindowGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Stacking order; higher is in front. */
  z: number;
  /** Minimised to the taskbar. */
  min: boolean;
  /** What it shows: `root` (Documents), a folder id, or `trash`. */
  folder: string;
}

/** Mirrors `DESKTOP_LAYOUT_VIEW_MODES` (@fias/db-types). */
export const VIEW_MODES = [
  'icons-xl',
  'icons-lg',
  'icons-md',
  'icons-sm',
  'list',
  'details',
  'columns',
  'gallery',
] as const;
export type ViewMode = (typeof VIEW_MODES)[number];
/** Mirrors `DESKTOP_LAYOUT_SORT_KEYS`. */
export const SORT_KEYS = ['name', 'modified', 'type', 'size'] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type SortDirection = 'asc' | 'desc';

export interface FolderView {
  mode: ViewMode;
  sort: SortKey;
  dir: SortDirection;
}

export interface DesktopLayout {
  v: 1;
  /** `root` | a folder id → item id (document, folder, or `trash`) → cell. */
  containers: Record<string, Record<string, Cell>>;
  /** A window id → its geometry and the folder it shows. Present = open. */
  windows: Record<string, WindowGeometry>;
  /** `desktop` (the desktop's own icon size) | `root` (Documents in a
   *  window) | `trash` | a folder id → how it's viewed. Absent = the default. */
  views: Record<string, FolderView>;
}

/** The container key for the Holder root (the desktop itself). */
export const ROOT_CONTAINER = 'root';
/** The desktop's own view key (its icon size), distinct from `root` shown in a
 *  window. */
export const DESKTOP_VIEW_KEY = 'desktop';
/** The Trash's item key on the desktop, and its window's key. */
export const TRASH_KEY = 'trash';

/** Mirrors the server's schema (routes/vault/desktop-layout.ts). */
export const DESKTOP_LAYOUT_LIMITS = {
  upsertsPerPatch: 200,
  removalsPerPatch: 200,
  windowsPerPatch: 50,
  viewsPerPatch: 100,
  maxCoordinate: 4096,
  maxZ: 100_000,
} as const;

export function emptyLayout(): DesktopLayout {
  return { v: 1, containers: {}, windows: {}, views: {} };
}

/** A window saved before windows could navigate was keyed by what it showed. */
const LEGACY_WINDOW_ID = /^(vdf_[a-f0-9]{32}|trash)$/;

/**
 * A layout as the server sent it, made whole: `views` may be absent, and a
 * window saved before windows could navigate has no `folder` (its id was the
 * folder's) — the same reading the server's `normalizeLayout` applies.
 */
export function normalizeLayout(layout: Partial<DesktopLayout> | null | undefined): DesktopLayout {
  const windows: DesktopLayout['windows'] = {};
  for (const [id, stored] of Object.entries(layout?.windows ?? {})) {
    const folder =
      typeof stored?.folder === 'string' ? stored.folder : LEGACY_WINDOW_ID.test(id) ? id : null;
    if (folder) windows[id] = { ...stored, folder };
  }
  return {
    v: 1,
    containers: layout?.containers ?? {},
    windows,
    views: layout?.views ?? {},
  };
}

/** A fresh window id (`w_` + 12 base-36 characters). */
export function newWindowId(): string {
  let id = '';
  while (id.length < 12) id += Math.random().toString(36).slice(2);
  return `w_${id.slice(0, 12)}`;
}

// ---------------------------------------------------------------------------
// Patches — pending local changes, coalesced per key
// ---------------------------------------------------------------------------

/**
 * Changes not yet saved. Keyed so a later edit to the same icon or window
 * replaces an earlier one: `null` means remove. Two patches for different
 * keys commute, which is what makes merging safe on the server too.
 */
export interface LayoutPatch {
  /** `${container}|${item}` → cell, or null to remove the position. */
  positions: Map<string, Cell | null>;
  /** Window key → geometry, or null to close. */
  windows: Map<string, WindowGeometry | null>;
  /** Container → view, or null to go back to the default. */
  views: Map<string, FolderView | null>;
}

export function emptyPatch(): LayoutPatch {
  return { positions: new Map(), windows: new Map(), views: new Map() };
}

export function isEmptyPatch(patch: LayoutPatch): boolean {
  return patch.positions.size === 0 && patch.windows.size === 0 && patch.views.size === 0;
}

export function positionKey(container: string, item: string): string {
  return `${container}|${item}`;
}

function splitPositionKey(key: string): { container: string; item: string } {
  const bar = key.indexOf('|');
  return { container: key.slice(0, bar), item: key.slice(bar + 1) };
}

/** `later` applied on top of `earlier`: for each key, the later edit wins. */
export function mergePatches(earlier: LayoutPatch, later: LayoutPatch): LayoutPatch {
  const positions = new Map(earlier.positions);
  for (const [key, value] of later.positions) positions.set(key, value);
  const windows = new Map(earlier.windows);
  for (const [key, value] of later.windows) windows.set(key, value);
  const views = new Map(earlier.views);
  for (const [key, value] of later.views) views.set(key, value);
  return { positions, windows, views };
}

/** A new layout with `patch` applied. Empty containers are dropped. */
export function applyPatch(layout: DesktopLayout, patch: LayoutPatch): DesktopLayout {
  if (isEmptyPatch(patch)) return layout;
  const containers: DesktopLayout['containers'] = {};
  for (const [container, items] of Object.entries(layout.containers)) {
    containers[container] = { ...items };
  }
  for (const [key, cell] of patch.positions) {
    const { container, item } = splitPositionKey(key);
    if (cell) {
      containers[container] = { ...(containers[container] ?? {}), [item]: cell };
    } else if (containers[container]) {
      delete containers[container][item];
      if (Object.keys(containers[container]).length === 0) delete containers[container];
    }
  }
  const windows = { ...layout.windows };
  for (const [id, geometry] of patch.windows) {
    if (geometry) windows[id] = geometry;
    else delete windows[id];
  }
  const views = { ...layout.views };
  for (const [container, view] of patch.views) {
    if (view) views[container] = view;
    else delete views[container];
  }
  return { v: 1, containers, windows, views };
}

export interface LayoutPatchRequest {
  upserts: Array<{ container: string; item: string; c: number; r: number }>;
  removals: Array<{ container: string; item: string }>;
  windows: Array<
    | {
        id: string;
        x: number;
        y: number;
        w: number;
        h: number;
        z: number;
        min: boolean;
        folder: string;
      }
    | {
        id: string;
        remove: true;
      }
  >;
  views: Array<({ container: string } & FolderView) | { container: string; remove: true }>;
}

const clampInt = (value: number, max: number) =>
  Math.max(0, Math.min(max, Math.round(Number.isFinite(value) ? value : 0)));

/**
 * The request bodies that save `patch`, each within the server's per-patch
 * limits. Sent one after another; every body is independently safe to apply
 * (upserts and removals are idempotent), so a failure part-way loses nothing
 * a retry won't redo.
 */
export function patchRequests(patch: LayoutPatch): LayoutPatchRequest[] {
  const max = DESKTOP_LAYOUT_LIMITS.maxCoordinate;
  const upserts: LayoutPatchRequest['upserts'] = [];
  const removals: LayoutPatchRequest['removals'] = [];
  for (const [key, cell] of patch.positions) {
    const { container, item } = splitPositionKey(key);
    if (cell) upserts.push({ container, item, c: clampInt(cell.c, max), r: clampInt(cell.r, max) });
    else removals.push({ container, item });
  }
  const windows: LayoutPatchRequest['windows'] = [];
  for (const [id, g] of patch.windows) {
    windows.push(
      g
        ? {
            id,
            x: clampInt(g.x, max),
            y: clampInt(g.y, max),
            w: Math.max(1, clampInt(g.w, max)),
            h: Math.max(1, clampInt(g.h, max)),
            z: clampInt(g.z, DESKTOP_LAYOUT_LIMITS.maxZ),
            min: g.min,
            folder: g.folder,
          }
        : { id, remove: true },
    );
  }
  const views: LayoutPatchRequest['views'] = [];
  for (const [container, view] of patch.views) {
    views.push(view ? { container, ...view } : { container, remove: true });
  }
  const requests: LayoutPatchRequest[] = [];
  let u = 0;
  let r = 0;
  let w = 0;
  let v = 0;
  while (u < upserts.length || r < removals.length || w < windows.length || v < views.length) {
    requests.push({
      upserts: upserts.slice(u, u + DESKTOP_LAYOUT_LIMITS.upsertsPerPatch),
      removals: removals.slice(r, r + DESKTOP_LAYOUT_LIMITS.removalsPerPatch),
      windows: windows.slice(w, w + DESKTOP_LAYOUT_LIMITS.windowsPerPatch),
      views: views.slice(v, v + DESKTOP_LAYOUT_LIMITS.viewsPerPatch),
    });
    u += DESKTOP_LAYOUT_LIMITS.upsertsPerPatch;
    r += DESKTOP_LAYOUT_LIMITS.removalsPerPatch;
    w += DESKTOP_LAYOUT_LIMITS.windowsPerPatch;
    v += DESKTOP_LAYOUT_LIMITS.viewsPerPatch;
  }
  return requests;
}

// ---------------------------------------------------------------------------
// Auto-arrange — deterministic, and never reflowing what's on screen
// ---------------------------------------------------------------------------

export const cellIndex = (cell: Cell, columns: number) => cell.r * columns + cell.c;
export const cellAt = (index: number, columns: number): Cell => ({
  c: index % columns,
  r: Math.floor(index / columns),
});

/**
 * Where every item in a container is drawn.
 *
 * `order` is the container's items in their auto-arrange order (callers sort
 * by kind, name, then id, so the same files lay out the same way on every
 * machine). Then:
 *   1. stored positions first — in `order`, so on a collision the item that
 *      sorts first keeps the cell and the other auto-arranges;
 *   2. then `sticky` — cells this session already auto-assigned, so loading
 *      another page never moves an icon the person is looking at;
 *   3. then everything else, into the first free cells, row by row.
 *
 * A stored cell outside the current column count (a narrower screen) is
 * ignored for drawing only — the stored layout is never rewritten for it.
 * `sticky` is updated in place with the cells step 3 hands out.
 */
export function arrangeContainer(
  order: readonly string[],
  stored: Readonly<Record<string, Cell>> | undefined,
  columns: number,
  sticky: Map<string, Cell> = new Map(),
  /** The n-th free slot for step 3 — row by row from the top-left unless
   *  told otherwise (a Mac desktop fills columns from the top-right). */
  slot?: (index: number) => Cell,
): Map<string, Cell> {
  const cols = Math.max(1, Math.floor(columns));
  const placed = new Map<string, Cell>();
  const taken = new Set<number>();
  const claim = (key: string, cell: Cell) => {
    placed.set(key, cell);
    taken.add(cellIndex(cell, cols));
  };
  const fits = (cell: Cell | undefined): cell is Cell =>
    !!cell && cell.c >= 0 && cell.r >= 0 && cell.c < cols && !taken.has(cellIndex(cell, cols));

  for (const key of order) {
    const cell = stored?.[key];
    if (fits(cell)) claim(key, cell);
  }
  for (const key of order) {
    if (placed.has(key)) continue;
    const cell = sticky.get(key);
    if (fits(cell)) claim(key, cell);
  }
  const slotAt = slot ?? ((index: number) => cellAt(index, cols));
  const inGrid = (cell: Cell) => cell.c >= 0 && cell.c < cols && cell.r >= 0;
  let cursor = 0;
  let fallback = 0;
  for (const key of order) {
    if (placed.has(key)) continue;
    let cell = slotAt(cursor);
    while (inGrid(cell) && taken.has(cellIndex(cell, cols))) {
      cursor += 1;
      cell = slotAt(cursor);
    }
    if (inGrid(cell)) {
      cursor += 1;
    } else {
      // A custom order ran off the grid (a Mac desktop with more icons than
      // fit in its columns): the rest take free cells row by row.
      while (taken.has(fallback)) fallback += 1;
      cell = cellAt(fallback, cols);
    }
    claim(key, cell);
    sticky.set(key, cell);
  }
  return placed;
}

/** The free cell nearest `target` (by ring distance, then row-major), within
 *  `columns`. For dropping an icon where it was let go. */
export function nearestFreeCell(target: Cell, taken: ReadonlySet<number>, columns: number): Cell {
  const cols = Math.max(1, Math.floor(columns));
  const start: Cell = { c: Math.min(Math.max(0, target.c), cols - 1), r: Math.max(0, target.r) };
  if (!taken.has(cellIndex(start, cols))) return start;
  for (let ring = 1; ring < 10_000; ring += 1) {
    let best: Cell | null = null;
    for (let dr = -ring; dr <= ring; dr += 1) {
      for (let dc = -ring; dc <= ring; dc += 1) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== ring) continue;
        const cell = { c: start.c + dc, r: start.r + dr };
        if (cell.c < 0 || cell.c >= cols || cell.r < 0) continue;
        if (taken.has(cellIndex(cell, cols))) continue;
        if (!best || cellIndex(cell, cols) < cellIndex(best, cols)) best = cell;
      }
    }
    if (best) return best;
  }
  return start;
}

/** Sort key for auto-arrange: kind rank, then name (case-insensitive), then
 *  id — total and stable. */
export function compareArrangeOrder(
  a: { rank: number; name: string; key: string },
  b: { rank: number; name: string; key: string },
): number {
  if (a.rank !== b.rank) return a.rank - b.rank;
  const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
  if (byName !== 0) return byName;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Windows
// ---------------------------------------------------------------------------

export const MIN_WINDOW = { w: 280, h: 200 } as const;

/**
 * A window's geometry as DRAWN inside a `bounds`-sized area: at least the
 * minimum size, no bigger than the area, and fully on screen. Never written
 * back — a laptop session must not shrink the big-monitor arrangement.
 */
export function clampWindow(
  g: WindowGeometry,
  bounds: { width: number; height: number },
): WindowGeometry {
  const maxW = Math.max(MIN_WINDOW.w, bounds.width);
  const maxH = Math.max(MIN_WINDOW.h, bounds.height);
  const w = Math.min(Math.max(g.w, MIN_WINDOW.w), maxW);
  const h = Math.min(Math.max(g.h, MIN_WINDOW.h), maxH);
  const x = Math.min(Math.max(0, g.x), Math.max(0, bounds.width - w));
  const y = Math.min(Math.max(0, g.y), Math.max(0, bounds.height - h));
  return { ...g, x, y, w, h };
}

/** Where a newly opened window goes: cascaded from the top-left by how many
 *  are already open. */
export function cascadeWindow(
  openCount: number,
  z: number,
  bounds: { width: number; height: number },
  folder: string,
): WindowGeometry {
  const w = Math.min(720, Math.max(MIN_WINDOW.w, bounds.width - 80));
  const h = Math.min(460, Math.max(MIN_WINDOW.h, bounds.height - 80));
  const step = 28 * (openCount % 8);
  return clampWindow({ x: 48 + step, y: 32 + step, w, h, z, min: false, folder }, bounds);
}

/** The next z above every open window. */
export function nextZ(windows: Readonly<Record<string, WindowGeometry>>): number {
  let max = 0;
  for (const g of Object.values(windows)) max = Math.max(max, g.z);
  return Math.min(DESKTOP_LAYOUT_LIMITS.maxZ, max + 1);
}
